import { useEffect, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { countdownParts } from '../utils/comingSoon';
import type { CountdownParts } from '../utils/comingSoon';

/**
 * A live countdown to `releaseAt` (unix seconds), or null when there is none.
 *
 * It ticks once a second, lined up with the wall clock's second boundary so
 * several countdowns on one page change together rather than in a ripple. The
 * timer stops by itself once the release time has passed: from then on the
 * answer ("Opening any moment") no longer changes, so nothing needs to tick.
 *
 * `still` reports the reader's reduced-motion preference. A ticking second is
 * movement in the corner of the eye, so callers use it to show minutes only.
 * The value still updates each second either way; it is the display that
 * keeps still.
 */
export function useCountdown(releaseAt: number | null): { parts: CountdownParts | null; still: boolean } {
  const still = Boolean(useReducedMotion());
  const [now, setNow] = useState(Date.now);

  useEffect(() => {
    if (releaseAt === null || !Number.isFinite(releaseAt)) return;
    setNow(Date.now());
    if (releaseAt * 1000 <= Date.now()) return;
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      const current = Date.now();
      setNow(current);
      if (releaseAt * 1000 <= current) return;
      timer = setTimeout(tick, 1000 - (current % 1000) + 5);
    };
    timer = setTimeout(tick, 1000 - (Date.now() % 1000) + 5);
    return () => clearTimeout(timer);
  }, [releaseAt]);

  return {
    parts: releaseAt === null || !Number.isFinite(releaseAt) ? null : countdownParts(releaseAt, now),
    still,
  };
}
